'use client';

import { getPublicImageUrl } from '@/lib/utils/image-url';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertCircle, CheckCircle2, ExternalLink, Info, Link2, X, XCircle } from 'lucide-react';
import { useState } from 'react';

interface ImageSEOBadgeProps {
  pageUrl?: string;
  title?: string;
  caption?: string;
  description?: string;
  altText?: string;
  imageUrl?: string; // The actual image URL
  dimensions?: {
    width: number;
    height: number;
  };
  type?: 'image' | 'video' | 'document';
}

interface MissingField {
  name: string;
  description: string;
  isCritical: boolean;
}

interface FilledField {
  name: string;
  value: string;
  isCritical: boolean;
}

/**
 * Badge component that shows SEO status for images
 * Green = Fully configured, Yellow = Partial, Red = Missing critical fields
 * Clickable to show popup with missing fields information
 */
export default function ImageSEOBadge({
  pageUrl,
  title,
  caption,
  description,
  altText,
  imageUrl,
  dimensions,
  type,
}: ImageSEOBadgeProps) {
  const [showModal, setShowModal] = useState(false);

  // Only show badge for images
  if (type !== 'image') {
    return null;
  }

  // Check SEO completeness
  const hasPageUrl = !!pageUrl;
  const hasAltText = !!altText;
  const hasTitle = !!title;
  const hasCaption = !!caption;
  const hasDescription = !!description;
  const hasDimensions = !!(dimensions?.width && dimensions?.height);

  // Calculate score (0-6)
  const score = [
    hasPageUrl,
    hasAltText,
    hasTitle,
    hasCaption,
    hasDescription,
    hasDimensions,
  ].filter(Boolean).length;

  // Determine status
  let status: 'complete' | 'partial' | 'missing' = 'missing';
  let statusColor = 'bg-red-100 text-red-700 border-red-300';
  let statusIcon = <XCircle className="h-3 w-3" />;
  let statusText = 'SEO Missing';

  if (score >= 5) {
    status = 'complete';
    statusColor = 'bg-green-100 text-green-700 border-green-300';
    statusIcon = <CheckCircle2 className="h-3 w-3" />;
    statusText = 'SEO Ready';
  } else if (score >= 3) {
    status = 'partial';
    statusColor = 'bg-yellow-100 text-yellow-700 border-yellow-300';
    statusIcon = <AlertCircle className="h-3 w-3" />;
    statusText = 'SEO Partial';
  }

  // Critical fields check (pageUrl and altText are most important)
  const hasCriticalFields = hasPageUrl && hasAltText;

  // Get missing fields
  const missingFields: MissingField[] = [];

  if (!hasPageUrl) {
    missingFields.push({
      name: 'Page URL',
      description: 'The URL of the page where this image is used (e.g., /blogs/my-post-slug)',
      isCritical: true,
    });
  }
  if (!hasAltText) {
    missingFields.push({
      name: 'Alt Text',
      description: 'Descriptive alternative text for the image (required for accessibility and SEO)',
      isCritical: true,
    });
  }
  if (!hasTitle) {
    missingFields.push({
      name: 'Title',
      description: 'A descriptive title for the image',
      isCritical: false,
    });
  }
  if (!hasCaption) {
    missingFields.push({
      name: 'Caption',
      description: 'A caption that describes the image content',
      isCritical: false,
    });
  }
  if (!hasDescription) {
    missingFields.push({
      name: 'Description',
      description: 'A detailed description of the image content',
      isCritical: false,
    });
  }
  if (!hasDimensions) {
    missingFields.push({
      name: 'Dimensions',
      description: 'Width and height of the image (usually auto-detected)',
      isCritical: false,
    });
  }

  // Get filled fields for "SEO Ready" status
  const filledFields: FilledField[] = [];

  if (hasPageUrl && pageUrl) {
    filledFields.push({
      name: 'Page URL',
      value: pageUrl,
      isCritical: true,
    });
  }
  if (hasAltText && altText) {
    filledFields.push({
      name: 'Alt Text',
      value: altText,
      isCritical: true,
    });
  }
  if (hasTitle && title) {
    filledFields.push({
      name: 'Title',
      value: title,
      isCritical: false,
    });
  }
  if (hasCaption && caption) {
    filledFields.push({
      name: 'Caption',
      value: caption,
      isCritical: false,
    });
  }
  if (hasDescription && description) {
    filledFields.push({
      name: 'Description',
      value: description,
      isCritical: false,
    });
  }
  if (hasDimensions && dimensions) {
    filledFields.push({
      name: 'Dimensions',
      value: `${dimensions.width} × ${dimensions.height}px`,
      isCritical: false,
    });
  }

  const baseUrl = typeof window !== 'undefined'
    ? window.location.origin
    : (process.env.NEXT_PUBLIC_APP_URL || 'https://www.makemybrandlabs.com');

  // Extract blog slug from pageUrl to construct proper image path
  let correctedImageUrl = imageUrl || '';
  if (pageUrl && imageUrl) {
    // Extract slug from pageUrl (e.g., /blogs/my-blog-slug -> my-blog-slug)
    const slugMatch = pageUrl.match(/\/(?:blogs|news-pr|thought-leadership)\/([^\/\?]+)/);
    if (slugMatch && slugMatch[1]) {
      const blogSlug = slugMatch[1];
      // Check if imageUrl already includes the slug, if not, add it
      const imagePath = imageUrl.replace(/^\/uploads\//, '');
      const pathParts = imagePath.split('/');

      // If the path is like "blogs/filename.png" and doesn't include the slug, add it
      if (pathParts.length === 2 && pathParts[0] === 'blogs' && !pathParts[1].includes(blogSlug)) {
        correctedImageUrl = `/uploads/blogs/${blogSlug}/${pathParts[1]}`;
      }
    }
  }

  const absoluteImageUrl = correctedImageUrl ? getPublicImageUrl(correctedImageUrl) : '';
  const absolutePageUrl = pageUrl
    ? (pageUrl.startsWith('http') ? pageUrl : `${baseUrl}${pageUrl.startsWith('/') ? pageUrl : `/${pageUrl}`}`)
    : '';

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${statusColor} ${!hasCriticalFields ? 'animate-pulse' : ''} cursor-pointer hover:opacity-80 transition-opacity`}
        title={status === 'complete' ? 'Click to see SEO details' : 'Click to see what fields need to be added'}
      >
        {statusIcon}
        <span>{statusText}</span>
      </button>

      {/* SEO Status Popup */}
      <Dialog.Root open={showModal} onOpenChange={setShowModal}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] animate-in fade-in" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-white rounded-xl shadow-2xl z-[201] overflow-hidden border border-gray-200">
            {/* Header */}
            <div className={`px-6 py-4 border-b border-gray-200 ${status === 'complete'
              ? 'bg-gradient-to-r from-green-50 to-emerald-50'
              : status === 'partial'
                ? 'bg-gradient-to-r from-yellow-50 to-orange-50'
                : 'bg-gradient-to-r from-red-50 to-orange-50'
              }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${status === 'complete'
                    ? 'bg-green-100'
                    : status === 'partial'
                      ? 'bg-yellow-100'
                      : 'bg-red-100'
                    }`}>
                    {status === 'complete' ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    ) : status === 'partial' ? (
                      <AlertCircle className="h-5 w-5 text-yellow-600" />
                    ) : (
                      <Info className="h-5 w-5 text-red-600" />
                    )}
                  </div>
                  <div>
                    <Dialog.Title className="text-lg font-bold text-gray-900">
                      {status === 'complete' ? 'SEO Ready' : status === 'partial' ? 'SEO Partial' : 'SEO Fields Missing'}
                    </Dialog.Title>
                    <p className="text-sm text-gray-600 mt-0.5">
                      {status === 'complete'
                        ? `All SEO fields configured (${score}/6 complete)`
                        : `Add these fields to improve SEO (${score}/6 complete)`
                      }
                    </p>
                  </div>
                </div>
                <Dialog.Close asChild>
                  <button className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-md hover:bg-gray-100">
                    <X className="h-5 w-5" />
                  </button>
                </Dialog.Close>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {/* Image URL Section */}
              {absoluteImageUrl && (
                <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Link2 className="h-4 w-4" />
                    Image URL
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <a
                        href={absoluteImageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 break-all flex items-center gap-2 hover:underline flex-1 min-w-0"
                      >
                        <span className="truncate">{absoluteImageUrl}</span>
                        <ExternalLink className="h-3 w-3 flex-shrink-0" />
                      </a>
                    </div>
                    <div className="text-xs text-gray-500 space-y-1">
                      <p>Click to view image in new tab</p>
                      {correctedImageUrl && (
                        <p className="text-gray-400 font-mono text-[10px] break-all">
                          Server path: uploads/{correctedImageUrl.replace(/^\/uploads\//, '')}
                        </p>
                      )}
                      {correctedImageUrl !== imageUrl && imageUrl && (
                        <p className="text-blue-600 text-[10px]">
                          ℹ️ URL corrected to include blog slug: {correctedImageUrl}
                        </p>
                      )}
                      <p className="text-orange-600">
                        ⚠️ If you see &quot;File not found&quot;, check if the file exists in the uploads directory.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Page URL Section */}
              {absolutePageUrl && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Link2 className="h-4 w-4" />
                    Page URL (Click to view page)
                  </h4>
                  <a
                    href={absolutePageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 break-all flex items-center gap-2 hover:underline"
                  >
                    {absolutePageUrl}
                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                  </a>
                </div>
              )}

              {/* Fields Section */}
              {status === 'complete' ? (
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900 mb-3">Configured Fields:</h4>
                  {filledFields.map((field, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border-2 ${field.isCritical
                        ? 'bg-green-50 border-green-200'
                        : 'bg-gray-50 border-gray-200'
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`flex-shrink-0 mt-0.5 ${field.isCritical ? 'text-green-600' : 'text-gray-600'
                          }`}>
                          <CheckCircle2 className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-gray-900">
                              {field.name}
                            </h4>
                            {field.isCritical && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                Critical
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 break-words">
                            {field.value}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {missingFields.map((field, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border-2 ${field.isCritical
                        ? 'bg-red-50 border-red-200'
                        : 'bg-yellow-50 border-yellow-200'
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`flex-shrink-0 mt-0.5 ${field.isCritical ? 'text-red-600' : 'text-yellow-600'
                            }`}
                        >
                          {field.isCritical ? (
                            <XCircle className="h-5 w-5" />
                          ) : (
                            <AlertCircle className="h-5 w-5" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-gray-900">
                              {field.name}
                            </h4>
                            {field.isCritical && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                                Critical
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            {field.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Quick Tips */}
              {status !== 'complete' && (
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    Quick Tips
                  </h4>
                  <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                    <li>You need at least 5 out of 6 fields to get &quot;SEO Ready&quot; status</li>
                    <li>Page URL and Alt Text are critical - add these first!</li>
                    <li>Edit the image in Media Editor to add missing fields</li>
                  </ul>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
              <Dialog.Close asChild>
                <button className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors font-medium">
                  {status === 'complete' ? 'Close' : 'Got it'}
                </button>
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

